const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:8000';
const TOKEN_KEY = 'policyora_token';

function authHeaders(json = true): HeadersInit {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (json) headers['Content-Type'] = 'application/json';
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function parseResponse<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    let detail = `Request failed (${res.status})`;
    try { detail = JSON.parse(body).detail ?? detail; } catch { /* ignore */ }
    throw new Error(detail);
  }
  return res.json() as Promise<T>;
}

// --- Types ---

export interface GovernanceStatus {
  session_id: string;
  status: string;
  current_node: string | null;
}

export interface GovernanceQuestion {
  question_id: string;
  element_id?: string;
  dimension_id?: string;
  policy_id: string;
  question_text: string;
  answer_text?: string | null;
  question_type?: 'document_request' | 'standalone_question' | null;
  document_type?: string | null;
  document_description?: string | null;
  resolves_question_ids?: string[] | null;
  resolves_dimension_ids?: string[] | null;
}

export interface AreaScore {
  score: number;
  coverage: number;
  dimensions_met: number;
  dimensions_partial: number;
  dimensions_missing: number;
}

export interface DimensionResult {
  dimension_id: string;
  status: string;
  evidence_used: string[];
  gap: string | null;
  severity: string | null;
  score_contribution: number;
  recommendation: string | null;
  question?: string;
  area?: string;
}

export interface PolicyCoverage {
  raw_dimensions: number;
  covered: number;
  met: number;
  partial: number;
  missing: number;
}

export interface DeferredSummary {
  total: number;
  by_area: Record<string, number>;
}

export interface MergeStats {
  raw_count: number;
  post_dedup_count: number;
  post_filter_count?: number;
  deferred_count?: number;
  per_area_counts: Record<string, number>;
  deferred_by_area?: Record<string, number>;
}

export interface ResolutionStats {
  total_raw_questions: number;
  resolved_by_existing_docs: number;
  resolved_by_uploaded_docs: number;
  resolved_by_text_answers: number;
  document_requests_sent: number;
  standalone_questions_sent: number;
}

export interface GovernanceResult {
  company_id: string;
  session_id: string;
  engine: string;
  overall_score: number;
  health_rating: string;
  area_scores: Record<string, AreaScore>;
  dimension_results: DimensionResult[];
  critical_gaps: (DimensionResult & { question: string; area: string })[];
  high_gaps: (DimensionResult & { question: string; area: string })[];
  top_recommendations: { recommendation: string; severity: string | null; dimension_id: string; question: string }[];
  evidence_map: Record<string, { question: string; evidence_used: string[]; doc_sources: string[] }>;
  assessed_at: string;
  hitl_iterations: number;
  dimensions_from_cache: number;
  areas_from_cache: number;
  total_elements: number;
  elements_met: number;
  elements_partial: number;
  elements_missing: number;
  coverage_score: number;
  ownership_score: number;
  policy_coverage?: Record<string, PolicyCoverage>;
  deferred_summary?: DeferredSummary;
  merge_stats?: MergeStats;
  resolution_stats?: ResolutionStats;
}

// --- Dashboard types ---

export interface AssessmentSummary {
  assessment_id: string | null;
  session_id: string;
  version: number | null;
  jurisdiction: string | null;
  overall_score: number | null;
  health_rating: string | null;
  status: string;
  current_node: string | null;
  total_regulations: number | null;
  total_dimensions?: number | null;
  dimensions_met?: number | null;
  dimensions_partial?: number | null;
  dimensions_missing?: number | null;
  coverage_score?: number | null;
  hitl_iterations?: number | null;
  assessed_at: string | null;
  created_at: string | null;
}

export interface AssessmentDetail {
  assessment_id: string;
  version: number;
  session_id: string;
  jurisdiction: string | null;
  overall_score: number | null;
  health_rating: string | null;
  status: string;
  assessed_at: string | null;
  final_result: GovernanceResult | null;
  questions: LatestResultQuestion[];
  dimension_details: Record<string, DimensionDetail>;
}

export interface AssessmentRegulation {
  artifact_id: string;
  regulation_name: string | null;
  jurisdiction: string | null;
  raw_dimensions: number;
  covered_dimensions: number;
  dimensions_met: number;
  dimensions_partial: number;
  dimensions_missing: number;
  compliance_score: number;
}

export interface AssessmentAreaScore {
  area: string;
  area_label: string | null;
  score: number;
  coverage: number;
  dimensions_met: number;
  dimensions_partial: number;
  dimensions_missing: number;
}

export interface AssessmentGap {
  dimension_id: string;
  area: string;
  question: string;
  status: string;
  gap: string | null;
  severity: string | null;
  score_contribution: number;
  recommendation: string | null;
  evidence_used: string[] | null;
  source_artifact_ids: string[] | null;
}

export interface AssessmentQuestionAnswer {
  question_id: string;
  dimension_id: string | null;
  policy_id: string | null;
  question_text: string;
  question_type: string | null;
  answer_text: string | null;
  answer_type: string | null;
  file_s3_key: string | null;
  file_name: string | null;
}

export interface PreviousAnswers {
  from_version: number | null;
  from_assessment_id: string | null;
  answers: AssessmentQuestionAnswer[];
}

// --- Existing API ---

export async function getGovernanceStatus(sessionId: string): Promise<GovernanceStatus> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/status/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<GovernanceStatus>(res);
}

export async function getGovernanceQuestions(sessionId: string): Promise<GovernanceQuestion[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/questions/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<GovernanceQuestion[]>(res);
}

export async function submitGovernanceAnswers(
  sessionId: string,
  answers: { question_id: string; answer_text: string }[]
): Promise<{ status: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/answers/${sessionId}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ answers }),
  });
  return parseResponse<{ status: string }>(res);
}

export async function getGovernanceResults(sessionId: string): Promise<GovernanceResult | { status: string }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/results/${sessionId}`, {
    headers: authHeaders(),
  });
  return parseResponse<GovernanceResult | { status: string }>(res);
}

export async function uploadGovernanceDocument(
  sessionId: string,
  questionId: string,
  file: File,
): Promise<{ status: string; question_id: string; s3_key: string; filename: string }> {
  const form = new FormData();
  form.append('question_id', questionId);
  form.append('file', file);

  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}/api/v1/governance/upload-document/${sessionId}`, {
    method: 'POST',
    headers,
    body: form,
  });
  return parseResponse(res);
}

// --- Dashboard API ---

export async function listAssessments(): Promise<AssessmentSummary[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/assessments`, {
    headers: authHeaders(),
  });
  return parseResponse<AssessmentSummary[]>(res);
}

export async function getAssessmentDetail(assessmentId: string): Promise<AssessmentDetail> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/assessments/${assessmentId}`, {
    headers: authHeaders(),
  });
  return parseResponse<AssessmentDetail>(res);
}

export async function getAssessmentRegulations(assessmentId: string): Promise<AssessmentRegulation[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/assessments/${assessmentId}/regulations`, {
    headers: authHeaders(),
  });
  return parseResponse<AssessmentRegulation[]>(res);
}

export async function getAssessmentAreas(assessmentId: string): Promise<AssessmentAreaScore[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/assessments/${assessmentId}/areas`, {
    headers: authHeaders(),
  });
  return parseResponse<AssessmentAreaScore[]>(res);
}

export async function getAssessmentGaps(assessmentId: string, severity?: string): Promise<AssessmentGap[]> {
  const params = severity ? `?severity=${severity}` : '';
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/assessments/${assessmentId}/gaps${params}`, {
    headers: authHeaders(),
  });
  return parseResponse<AssessmentGap[]>(res);
}

export async function getAssessmentQuestions(assessmentId: string): Promise<AssessmentQuestionAnswer[]> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/assessments/${assessmentId}/questions`, {
    headers: authHeaders(),
  });
  return parseResponse<AssessmentQuestionAnswer[]>(res);
}

export async function getPreviousAnswers(): Promise<PreviousAnswers> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/previous-answers`, {
    headers: authHeaders(),
  });
  return parseResponse<PreviousAnswers>(res);
}

export interface AnswerFile {
  id: number;
  file_s3_key: string;
  file_name: string | null;
  file_size_bytes: number | null;
  content_type: string | null;
}

export interface AnswerVersion {
  version_id: number;
  version: number;
  answer_text: string | null;
  created_at: string | null;
  files: AnswerFile[];
}

export interface LatestResultQuestion {
  id: number;
  question_id: string;
  dimension_id: string | null;
  policy_id: string | null;
  question_text: string;
  question_type: string | null;
  latest_answer: AnswerVersion | null;
  answer_versions: AnswerVersion[];
}

export interface DimensionDetail {
  dimension_id: string;
  area: string;
  question: string;
  status: string;
  score_contribution: number | null;
  severity: string | null;
  policy_reference: string | null;
  company_evidence_summary: string | null;
  reasoning: string | null;
  gap: string | null;
  recommendation: string | null;
  evidence_used: string[] | null;
  doc_sources: string[] | null;
  source_policy_id: string | null;
  source_policy_name: string | null;
}

export interface LatestResult {
  assessment_id: string;
  version: number;
  jurisdiction: string | null;
  assessed_at: string | null;
  final_result: GovernanceResult;
  questions: LatestResultQuestion[];
  dimension_details: Record<string, DimensionDetail>;
}

export async function getLatestResult(): Promise<LatestResult> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/latest-result`, {
    headers: authHeaders(),
  });
  return parseResponse<LatestResult>(res);
}

export async function saveAnswerVersion(
  questionRowId: number,
  answerText: string | null,
): Promise<AnswerVersion> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/questions/${questionRowId}/answer`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify({ answer_text: answerText }),
  });
  return parseResponse<AnswerVersion>(res);
}

export async function uploadAnswerFile(
  questionRowId: number,
  versionId: number,
  file: File,
): Promise<AnswerFile> {
  const form = new FormData();
  form.append('file', file);

  const token = localStorage.getItem(TOKEN_KEY);
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(
    `${API_BASE_URL}/api/v1/governance/questions/${questionRowId}/answer/${versionId}/upload`,
    { method: 'POST', headers, body: form },
  );
  return parseResponse<AnswerFile>(res);
}

export async function getAnswerHistory(
  questionRowId: number,
): Promise<{ question_id: string; question_text: string; versions: AnswerVersion[] }> {
  const res = await fetch(`${API_BASE_URL}/api/v1/governance/questions/${questionRowId}/history`, {
    headers: authHeaders(),
  });
  return parseResponse(res);
}
